// The test valley (gates A2.2 and A2.3, `?scene=valley`): the course (course.ts), the hero as a capsule placeholder (the
// figure stream builds the Wanderer) on @rpg/controller, and a stub mount the hero can hand control to and get it back
// from. The same machinery carries the variants (the world, the labs); a variant without a mount spawn has no mount at
// all (no body, controller, component or handoff target): the world since decision 0084, which keeps the stub mount
// on the A2.3 course. All of it runs in the spike's one kernel game as the "valley" module:
//   valley:controllers (order 0)  read the step's input, hand control on `interact` (when there is a mount), step the
//                                 mount then the hero (a rider follows its mount, so the mount moves first);
//   valley:physics step (order 100) the Rapier step.
// The controllers' states, the handoff and the camera yaw are kernel components (saved, hashed); the controllers and
// the handoff are logic over them. Input comes through the actions (`move`, `jump`, `sprint`, `glide`, `interact`),
// turned into world space with the camera's yaw (fixed for now: the camera stream, A2.4, will set it).
import type { ContentPack, GameModule, World } from "@rpg/kernel";
import type { ActionMap, Actions, RngStreams } from "@rpg/runtime";
import type { BodySpec, ColliderSpec, CollisionWorld, LayerTable } from "@rpg/collision";
import {
  ControlHandoff,
  NO_INPUT,
  airMode,
  createController,
  glideMode,
  groundMode,
  rightOf,
  stickToWorld,
  type Controller,
  type ControllerInput,
  type ControllerState,
  type HandoffState,
  type ModeChange,
  type MountedMode,
  type MoveDrive,
} from "@rpg/controller";
import type { GameEvents } from "../core";
import type { SceneDef, SceneName, SceneSetup } from "../sim/host";
import { MOUNT_SPAWN, SPAWN, VALLEY_LAYERS, buildCourse, terrainHeight, type Course } from "./course";
import type { ValleyTuning } from "./tuning";

/** The valley's own action map, used only where the game's input (src/input, gate A2.1, built from the content's
 *  bindings) isn't passed in: Node runs (tools/valley.test.ts). The names are the actions table's. Glide shares the
 *  jump's controls: press again in the air (in the content, the glide action `shares` jump). */
export const VALLEY_ACTIONS: ActionMap = {
  buttons: { jump: ["Space", "Pad0"], sprint: ["ShiftLeft", "ShiftRight", "Pad10"], glide: ["Space", "Pad0"], interact: ["KeyE", "Pad3"] },
  sticks: { move: { up: ["KeyW", "ArrowUp"], down: ["KeyS", "ArrowDown"], left: ["KeyA", "ArrowLeft"], right: ["KeyD", "ArrowRight"], vectors: ["PadLeft", "Touch:move"] } },
};

export const HERO_C = "valley/Hero";
export const MOUNT_C = "valley/Mount";
export const CONTROL_C = "valley/Control";
export const PHYSICAL_C = "sim/Physical";
/** The component types the state hash covers (a valley without a mount leaves out MOUNT_C). */
export const VALLEY_HASHED = [HERO_C, MOUNT_C, CONTROL_C, PHYSICAL_C] as const;
/** The mount's body id (in the collision world and the view). */
export const MOUNT_BODY = "mount";
export const VALLEY_SEED = 0x7a11e;

/** Who has control, the camera's yaw and profile: one component. */
export interface ControlState {
  handoff: HandoffState;
  /** The camera's yaw (the stick is relative to it). Saved: it decides the sim's next step. */
  cameraYaw: number;
  /** The camera profile of whoever has control. */
  profile: string;
}

/** One step's input as the valley reads it from the actions (the stick still in camera space). */
export interface ValleyInput {
  stick: { x: number; y: number };
  jump: boolean;
  jumpPressed: boolean;
  sprint: boolean;
  glide: boolean;
  glidePressed: boolean;
  interact: boolean;
}

/** Planted defects the valley owns (each beside what it breaks; the collision world's and the controller's are
 *  switched through here too). */
export const VALLEY_FAULTS = {
  /** Characters never snap to the ground (`@rpg/collision`): the route bot's grounded legs must go red. */
  noGroundSnap: false,
  /** A handoff never gives control back (`@rpg/controller`): the modes spec must go red. */
  handoffNeverReturns: false,
  /** No coyote jump (`@rpg/controller`): the feel spec's coyote gate must go red. */
  noCoyote: false,
  /** The hero's state is written 1 m under the terrain each step: the below-ground invariant must flag it. */
  heroBelowGround: false,
  /** The hero's controller doesn't step: with the stick held, the stuck invariant must flag it. */
  controllerSkipped: false,
  /** The camera's yaw is read live, not through the input tape: a replay with the camera turned differently must
   *  diverge (the valley camera spec's replay check goes red). */
  yawNotRecorded: false,
};
export type ValleyFaultName = keyof typeof VALLEY_FAULTS;

/** One mode change, logged for the specs (capped). */
export interface TraceEntry extends ModeChange {
  step: number;
  who: "hero" | "mount";
}

/** What the valley exposes to its handle, bots and feel probes once the host has built it. */
export interface ValleyRig {
  collision: CollisionWorld;
  actions: Actions;
  course: Course;
  tuning: ValleyTuning;
  heroCtl: Controller;
  /** Null when the valley has no mount (a variant with `mountSpawn: null`). */
  mountCtl: Controller | null;
  handoff: ControlHandoff;
  hero(): ControllerState;
  /** The mount's state, or null when the valley has none. */
  mount(): ControllerState | null;
  control(): ControlState;
  /** The input the hero got last step (world space) and whether it had control. */
  heroInput(): ControllerInput;
  heroInControl(): boolean;
  /** Steps since boot (the kernel's own count). */
  steps(): number;
  dt: number;
  /** The last 500 mode changes, and the "controller:camera" events seen. */
  trace: TraceEntry[];
  /** The hero's mode changes counted by "from>to", since boot. */
  counts: Record<string, number>;
  cameraEvents: { profile: string; owner: string }[];
  /** Called at the start of each sim step, before the input is read (bots steer here). */
  drive: (() => void) | null;
  /** Replaces the input read from the actions for the next steps (the feel probes drive the controller exactly). */
  override: ValleyInput | null;
  /** The camera rig's yaw, when a camera is mounted (src/valley/camera.ts): sampled into the sim each step (recorded
   *  with the input) while no bot or probe is driving; they steer by the yaw they set with `setCameraYaw`. */
  camera: { yaw(): number; setYaw(yaw: number): void } | null;
  /** The sim keeps its own camera yaw (set with `setCameraYaw`) instead of the rig's: a spec that steers the stick in
   *  world terms holds it, since the rig's drift back turns the yaw while the hero runs across the view. */
  yawHeld: boolean;
  /** Called after each sim step's tick. */
  observe: (() => void) | null;
  /** The terrain's height under (x, z), from the heightfield collider itself. */
  terrainAt(x: number, z: number): number;
  /** Teleport the hero (at rest, standing, on foot, control back home). */
  warpHero(x: number, y: number, z: number, yaw?: number): void;
}

const NO_VALLEY_INPUT: ValleyInput = { stick: { x: 0, y: 0 }, jump: false, jumpPressed: false, sprint: false, glide: false, glidePressed: false, interact: false };

/** A scene built on the valley's machinery (the hero on its controller, the camera, the look) with its own ground
 *  and parts: phase B's dojo (`?scene=dojo`, src/combat/dojo.ts), and the default valley with its fight
 *  (src/valley/fight.ts). */
export interface ValleyVariant {
  name: SceneName;
  layers: LayerTable;
  course(t: ValleyTuning["hero"]): Course;
  /** The ground's height under (x, z) before the broad phase exists (spawns). */
  height(x: number, z: number): number;
  spawn: { x: number; z: number; yaw: number };
  /** Where the stub mount stands, or null for none (the world, decision 0084: the mount stays on the A2.3 course). */
  mountSpawn: { x: number; z: number; yaw: number } | null;
  /** Extra colliders on the hero's body (a hurt sensor). */
  heroColliders?: ColliderSpec[];
  /** The physics solver's iterations (ragdolls want more than Rapier's default). */
  solverIterations?: number;
  /** The variant's own parts, built once the rig exists. */
  extend(o: { rig: ValleyRig; rng: RngStreams; actions: Actions }): ValleyExtension;
}

/** What a variant adds to the valley's one kernel game. */
export interface ValleyExtension {
  /** Its modules, registered after the valley's. */
  modules: GameModule<GameEvents>[];
  /** Called at the end of the valley's content build (the hero and the mount, when there is one, exist). */
  build(world: World): void;
  /** Its component types for the state hash. */
  hashed: readonly string[];
  /** The hero's move override for this step (a lunge, a roll, rooted while acting), or null for the stick; read in the
   *  controllers system. While it is set, a jump press stays in the buffer. */
  heroDrive(): MoveDrive | null;
  /** Whether the variant took this step's interact press (phase C: the interaction system fired a sign, a door, a
   *  herb): the valley's mount handoff (when there is a mount) then leaves it. Absent: never. */
  claimsInteract?(): boolean;
  /** Phase D1: the hero controller's per-step scales (a slow status on its speed; collected glyphs on its glide).
   *  Absent: none. */
  heroScale?(): ControllerInput["scale"];
}

/** Build the valley scene from the tuning (or a variant of it). `rig()` is valid once the host has called `setup`. */
export function createValley(tuning: ValleyTuning, variant?: ValleyVariant): { def: SceneDef; rig(): ValleyRig } {
  let rig: ValleyRig | null = null;
  const def: SceneDef = {
    name: variant?.name ?? "valley",
    layers: variant?.layers ?? VALLEY_LAYERS,
    seed: VALLEY_SEED,
    actions: VALLEY_ACTIONS,
    ...(variant?.solverIterations ? { solverIterations: variant.solverIterations } : {}),
    setup(collision, rng, actions) {
      const s = setupValley(collision, actions, tuning, variant, rng);
      rig = s.rig;
      return s.setup;
    },
  };
  return {
    def,
    rig: () => {
      if (!rig) throw new Error("valley: not set up yet");
      return rig;
    },
  };
}

function setupValley(collision: CollisionWorld, actions: Actions, tuning: ValleyTuning, variant: ValleyVariant | undefined, rng: RngStreams): { setup: SceneSetup; rig: ValleyRig } {
  const course = variant ? variant.course(tuning.hero) : buildCourse(tuning.hero);
  const groundHeight = variant?.height ?? terrainHeight;
  const spawn = variant?.spawn ?? SPAWN;
  // the course's own mount without a variant; a variant names its own, or null for none
  const mountSpawn = variant ? variant.mountSpawn : MOUNT_SPAWN;
  const hasMount = mountSpawn !== null;
  let ext: ValleyExtension | null = null;
  const dt = collision.dt;
  let world: World | null = null;
  const one = <T>(type: string): T => {
    const e = world!.query(type)[0];
    if (e === undefined) throw new Error(`valley: no ${type}`);
    return world!.getComponent<T>(e, type)!;
  };
  const hero = (): ControllerState => one<ControllerState>(HERO_C);
  const mount = (): ControllerState | null => (hasMount ? one<ControllerState>(MOUNT_C) : null);
  const control = (): ControlState => one<ControlState>(CONTROL_C);

  // the rider's mounted mode (the controller's slot, filled by the game): sit on the mount's seat, move with it
  const mounted: MountedMode = {
    id: "mounted",
    step(c) {
      const m = mount()!;
      c.place({ x: m.feet.x, y: m.feet.y + tuning.seatHeight, z: m.feet.z });
      c.s.vel = { ...m.vel };
      c.s.yaw = m.yaw;
      return null;
    },
  };

  const handoff = new ControlHandoff({ id: "hero", cameraProfile: "explore" }, hasMount ? [{ id: "mount", cameraProfile: "mounted" }] : []);
  let heroCtl: Controller | null = null;
  let mountCtl: Controller | null = null;
  let input: ValleyInput = NO_VALLEY_INPUT;
  let heroIn: ControllerInput = NO_INPUT;
  let heroControls = true;
  /** A save's hero was riding a mount this valley doesn't have (set by the slice, cleared after the restore). */
  let orphanRider = false;
  let steps = 0;
  const trace: TraceEntry[] = [];
  const counts: Record<string, number> = {};
  const cameraEvents: { profile: string; owner: string }[] = [];
  const log = (who: "hero" | "mount", ch: ModeChange[]): void => {
    for (const c of ch) {
      trace.push({ ...c, who, step: steps });
      if (trace.length > 500) trace.shift();
      if (who === "hero") counts[`${c.from}>${c.to}`] = (counts[`${c.from}>${c.to}`] ?? 0) + 1;
    }
  };

  // the heightfield collider itself (its triangles, not the smooth function they sample); before the first step the
  // broad phase is empty and rays find nothing, so the course's function answers then
  const terrainAt = (x: number, z: number): number => collision.castRay({ x, y: 100, z }, { x: 0, y: -1, z: 0 }, 200, { layers: ["terrain"] })?.point.y ?? groundHeight(x, z);

  const dismount = (h: ControllerState, m: ControllerState): void => {
    const r = rightOf(m.yaw);
    heroCtl!.warp(h, { x: m.feet.x + r.x * 1.4, y: m.feet.y, z: m.feet.z + r.z * 1.4 }, m.yaw);
  };

  const module: GameModule<GameEvents> = {
    id: "valley",
    version: "0.1.0",
    setup(ctx) {
      handoff.onChange((c) => {
        cameraEvents.push({ profile: c.cameraProfile, owner: c.to });
        ctx.events.emit("controller:camera", { profile: c.cameraProfile, owner: c.to });
      });
      ctx.registerSystem(
        ({ dt: stepDt }) => {
          steps++;
          const h = hero();
          const m = mount();
          const ctl = control();
          // a variant's move override (combat's lunge or roll) replaces the stick, and holds the jump back
          const drive = ext?.heroDrive() ?? null;
          const ci: ControllerInput = {
            move: stickToWorld(input.stick, ctl.cameraYaw),
            jump: input.jump,
            jumpPressed: drive ? false : input.jumpPressed,
            sprint: input.sprint,
            glide: input.glide,
            glidePressed: drive ? false : input.glidePressed,
            ...(drive ? { drive } : {}),
            ...(ext?.heroScale ? { scale: ext.heroScale() } : {}),
          };
          // control handoff on `interact`: ride the mount when near it on foot; give control back when riding (no mount:
          // nothing to hand to)
          if (m && input.interact && !ext?.claimsInteract?.()) {
            if (handoff.controls(ctl.handoff, "hero")) {
              if (h.mode === "ground" && Math.hypot(h.feet.x - m.feet.x, h.feet.z - m.feet.z) <= tuning.reach) {
                handoff.give(ctl.handoff, "mount");
                h.riding = "mount";
              }
            } else if (handoff.release(ctl.handoff)) {
              h.riding = null;
              dismount(h, m);
            }
          }
          heroControls = handoff.controls(ctl.handoff, "hero");
          heroIn = heroControls ? ci : NO_INPUT;
          if (m) log("mount", mountCtl!.step(m, handoff.controls(ctl.handoff, "mount") ? ci : NO_INPUT, stepDt));
          // the planted defect sits here, beside the step it skips
          if (!VALLEY_FAULTS.controllerSkipped) log("hero", heroCtl!.step(h, heroIn, stepDt));
          ctl.profile = handoff.cameraProfile(ctl.handoff);
        },
        { name: "valley:controllers", order: 0 },
      );
      ctx.registerSystem(() => collision.step(), { name: "valley:physics step", order: 100 });
      // the slice: the hero, the mount (when there is one) and the control state. A save with a mount loads into a
      // valley without one (a world save from before decision 0084): its mount is ignored, control comes home, and a
      // hero saved riding it is set back on its feet once the physics is back (`afterRestore` below)
      ctx.registerSaveSlice({
        serialize: () => {
          const m = mount();
          return { hero: structuredClone(hero()), ...(m ? { mount: structuredClone(m) } : {}), control: structuredClone(control()) };
        },
        deserialize: (data) => {
          const d = data as { hero: ControllerState; mount?: ControllerState; control: ControlState };
          const m = mount();
          Object.assign(hero(), structuredClone(d.hero));
          if (m && d.mount) Object.assign(m, structuredClone(d.mount));
          Object.assign(control(), structuredClone(d.control));
          if (!m) {
            const ctl = control();
            orphanRider = ctl.handoff.stack.length > 1 || hero().riding !== null || hero().mode === "mounted";
            ctl.handoff.stack = [handoff.home.id];
            ctl.profile = handoff.cameraProfile(ctl.handoff);
          }
        },
      });
      ctx.registerAction("valley/input", (p) => void (input = p as ValleyInput));
      ctx.registerAction("valley/camera-yaw", (p) => void (control().cameraYaw = p as number));
    },
  };

  const t = tuning.hero;
  const heroBody: BodySpec = {
    id: "hero",
    kind: "kinematic",
    position: { x: spawn.x, y: t.halfHeight + t.radius, z: spawn.z },
    colliders: [{ shape: { type: "capsule", halfHeight: t.halfHeight, radius: t.radius }, layer: "hero" }, ...(variant?.heroColliders ?? [])],
  };
  const mt = tuning.mount;
  const mountBody: BodySpec | null = mountSpawn && {
    id: MOUNT_BODY,
    kind: "kinematic",
    position: { x: mountSpawn.x, y: mt.halfHeight + mt.radius, z: mountSpawn.z },
    colliders: [{ shape: { type: "capsule", halfHeight: mt.halfHeight, radius: mt.radius }, layer: "mount" }],
  };
  const bodies = [...course.bodies, heroBody, ...(mountBody ? [mountBody] : [])];

  const content: ContentPack<GameEvents> = {
    meta: { id: "valley-course", displayName: "The test valley", version: "0.1.0" },
    requiresModules: [{ id: "valley", versionRange: "^0.1.0" }],
    build(c) {
      world = c.world;
      for (const b of bodies) {
        collision.add(b);
        world.addComponent(world.createEntity(), PHYSICAL_C, { body: b.id });
      }
      for (const w of course.water) collision.addWater(w);
      // one physics step builds the broad phase, so rays and the characters see the course from the first sim step
      // (before it they find nothing, and the hero's first step reads as a fall)
      collision.step();
      heroCtl = createController({ world: collision, body: "hero", tuning: tuning.hero, modes: [groundMode(), airMode(), glideMode(), ...(hasMount ? [mounted] : [])], surfaces: tuning.surfaces });
      mountCtl = mountBody ? createController({ world: collision, body: MOUNT_BODY, tuning: tuning.mount, modes: [groundMode(), airMode()], surfaces: tuning.surfaces }) : null;
      heroCtl.faults.noCoyote = VALLEY_FAULTS.noCoyote;
      handoff.faults.neverReturns = VALLEY_FAULTS.handoffNeverReturns;
      collision.faults.noGroundSnap = VALLEY_FAULTS.noGroundSnap;
      // before the first step the broad phase is empty (no rays yet), so the start heights come from the course's function
      const hs = heroCtl.spawn({ x: spawn.x, y: groundHeight(spawn.x, spawn.z), z: spawn.z }, spawn.yaw);
      world.addComponent(world.createEntity(), HERO_C, hs);
      if (mountCtl && mountSpawn) world.addComponent(world.createEntity(), MOUNT_C, mountCtl.spawn({ x: mountSpawn.x, y: groundHeight(mountSpawn.x, mountSpawn.z), z: mountSpawn.z }, mountSpawn.yaw));
      world.addComponent<ControlState>(world.createEntity(), CONTROL_C, { handoff: handoff.init(), cameraYaw: Math.PI, profile: "explore" });
      ext?.build(world);
    },
  };

  const r: ValleyRig = {
    collision,
    actions,
    course,
    tuning,
    get heroCtl() {
      return heroCtl!;
    },
    get mountCtl() {
      return mountCtl;
    },
    handoff,
    hero,
    mount,
    control,
    heroInput: () => heroIn,
    heroInControl: () => heroControls,
    steps: () => steps,
    dt,
    trace,
    counts,
    cameraEvents,
    drive: null,
    override: null,
    camera: null,
    yawHeld: false,
    observe: null,
    terrainAt,
    warpHero(x, y, z, yaw = hero().yaw) {
      const ctl = control();
      while (ctl.handoff.stack.length > 1) ctl.handoff.stack.pop();
      ctl.profile = handoff.cameraProfile(ctl.handoff);
      heroCtl!.warp(hero(), { x, y, z }, yaw);
    },
  };

  // The jump press comes through the universal input buffer (gate A2.1) when the game's input has one: whoever has
  // control (the hero on foot, or the mount it rides) takes it at its first legal moment, which for a controller is
  // every step: the controller's own coyote time and 0.12 s landing buffer (gate A2.2) decide what the press does.
  // So today a press lands on the step it is made, as before; a state that can't take it (combat's recoveries and
  // hit-stop, phase B) will leave it in the slot to wait out the buffer's 0.22 s. Without a buffer (Node, the
  // scene's own action map) the press edge is read directly.
  const buffered = actions.buffer?.priority.includes("jump") === true;
  const takeJump = (): boolean => (buffered ? actions.buffer!.consume("jump") !== null : actions.pressed("jump"));

  ext = variant ? variant.extend({ rig: r, rng, actions }) : null;
  const setup: SceneSetup = {
    parts: { modules: [module, ...(ext?.modules ?? [])], content },
    hashed: [...VALLEY_HASHED.filter((c) => hasMount || c !== MOUNT_C), ...(ext?.hashed ?? [])],
    bodies,
    beforeTick(game) {
      // one source of truth for the yaw the stick is relative to: the camera rig's, recorded on the input tape
      if (r.camera && !r.drive && !r.override && !r.yawHeld) {
        // the planted defect sits here, beside the recording it skips
        const yaw = VALLEY_FAULTS.yawNotRecorded ? r.camera.yaw() : actions.sample("cameraYaw", r.camera.yaw);
        if (yaw !== control().cameraYaw) game.dispatch("valley/camera-yaw", yaw);
      }
      r.drive?.();
      const read: ValleyInput = r.override ?? {
        stick: actions.stick("move"),
        jump: actions.down("jump"),
        // a move in progress (combat's drive) leaves a jump press in the buffer for its first legal moment
        jumpPressed: ext?.heroDrive() ? false : takeJump(),
        sprint: actions.down("sprint"),
        glide: actions.down("glide"),
        glidePressed: actions.pressed("glide"),
        interact: actions.pressed("interact"),
      };
      game.dispatch("valley/input", read);
    },
    afterTick() {
      // the planted defect: the hero's state written under the terrain (the below-ground check must see it)
      if (VALLEY_FAULTS.heroBelowGround) hero().feet.y = terrainAt(hero().feet.x, hero().feet.z) - 1;
      r.observe?.();
    },
    afterRestore() {
      if (hasMount) return;
      // a save from a valley with a mount (a world save from before decision 0084): its physics brought the mount's
      // body back, and a hero saved riding it sits on the seat in a mode this valley doesn't have
      if (collision.has(MOUNT_BODY)) collision.remove(MOUNT_BODY);
      if (orphanRider) {
        const h = hero();
        heroCtl!.warp(h, { x: h.feet.x, y: terrainAt(h.feet.x, h.feet.z), z: h.feet.z }, h.yaw);
      }
      orphanRider = false;
    },
  };
  return { setup, rig: r };
}
